import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  approveDocumentRequestItem,
  createDocumentRequest,
  getDocumentRequest,
  listDocumentRequests,
  rejectDocumentRequestItem,
  submitDocumentRequestItem,
  type CreateDocumentRequestInput,
} from '@/lib/document-requests'
import type { DocumentRequestStatus } from '@riverpark/types'

export function useDocumentRequests(params?: { plotId?: string; status?: DocumentRequestStatus }) {
  return useQuery({
    queryKey: ['document-requests', params?.plotId ?? 'all', params?.status ?? 'all'],
    queryFn: () => listDocumentRequests(params),
  })
}

export function useDocumentRequest(id: string) {
  return useQuery({
    queryKey: ['document-requests', id],
    queryFn: () => getDocumentRequest(id),
  })
}

function useInvalidateDocumentRequests() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: ['document-requests'] })
    qc.invalidateQueries({ queryKey: ['documents'] })
    qc.invalidateQueries({ queryKey: ['notifications'] })
  }
}

export function useCreateDocumentRequest() {
  const invalidate = useInvalidateDocumentRequests()
  return useMutation({
    mutationFn: (input: CreateDocumentRequestInput) => createDocumentRequest(input),
    onSuccess: invalidate,
  })
}

export function useSubmitDocumentRequestItem() {
  const invalidate = useInvalidateDocumentRequests()
  return useMutation({
    mutationFn: ({ requestId, itemId, file }: { requestId: string; itemId: string; file: File }) =>
      submitDocumentRequestItem(requestId, itemId, file),
    onSuccess: invalidate,
  })
}

export function useApproveDocumentRequestItem() {
  const invalidate = useInvalidateDocumentRequests()
  return useMutation({
    mutationFn: ({
      requestId,
      itemId,
      note,
    }: {
      requestId: string
      itemId: string
      note?: string
    }) => approveDocumentRequestItem(requestId, itemId, note),
    onSuccess: invalidate,
  })
}

export function useRejectDocumentRequestItem() {
  const invalidate = useInvalidateDocumentRequests()
  return useMutation({
    mutationFn: ({
      requestId,
      itemId,
      note,
    }: {
      requestId: string
      itemId: string
      note: string
    }) => rejectDocumentRequestItem(requestId, itemId, note),
    onSuccess: invalidate,
  })
}
